/**
 * ListsHubTabs - Abas de navegação da Central de Importação de Listas
 *
 * Visual baseado em DashboardTabs (CRM). Renderiza apenas as abas
 * permitidas para o usuário (controlado pelo componente pai).
 */

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { Users, Briefcase, UserPlus, Contact } from 'lucide-react';

export type ListsHubTabKey = 'contacts' | 'deals' | 'affiliates' | 'customers';

export interface ListsHubTabDef {
  key: ListsHubTabKey;
  label: string;
  color: string; // cor do indicador (semantic-friendly via Tailwind tokens)
}

interface ListsHubTabsProps {
  tabs: ListsHubTabDef[];
  value: ListsHubTabKey;
  onChange: (key: ListsHubTabKey) => void;
}

const ICONS: Record<ListsHubTabKey, React.ComponentType<{ className?: string }>> = {
  contacts: Users,
  deals: Briefcase,
  affiliates: UserPlus,
  customers: Contact,
};

export function ListsHubTabs({ tabs, value, onChange }: ListsHubTabsProps) {
  if (tabs.length === 0) return null;

  return (
    <div className="flex items-center gap-2 w-full overflow-x-auto scrollbar-hide pb-1 -mb-1">
      <Tabs
        value={value}
        onValueChange={(v) => onChange(v as ListsHubTabKey)}
        className="w-full"
      >
        <TabsList className="h-9 bg-muted/50">
          {tabs.map((tab) => {
            const Icon = ICONS[tab.key];
            return (
              <TabsTrigger
                key={tab.key}
                value={tab.key}
                className={cn(
                  'px-4 text-sm whitespace-nowrap',
                  'data-[state=active]:bg-background',
                  'transition-all duration-200',
                )}
                style={{
                  borderBottomColor: value === tab.key ? tab.color : 'transparent',
                  borderBottomWidth: 2,
                }}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>
    </div>
  );
}
